<template>
   <div class="px-4 sm:px-6 lg:px-8">
      <div class="sm:flex sm:items-center">
         <div class="sm:flex-auto">
            <h1 class="text-base font-semibold leading-6 text-gray-900">Agents</h1>
            <p class="mt-2 text-sm text-gray-700">A list of all the agents in our database</p>
         </div>
      </div>
      <div class="mt-8 flow-root">
         <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="flex flex-col min-w-full py-2 align-middle sm:px-6 lg:px-8">
               <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <DataTable
                     ref="dt"
                     class="text-sm"
                     v-model:filters="filters" 
                     :value="agents"
                     paginator showGridlines :rows="10" 
                     dataKey="id"
                     filterDisplay="menu" 
                     :loading="loading"
                     :globalFilterFields="['name', 'email']"
                  >
                     <template #header>
                        <div class="flex gap-2 w-full justify-end">
                           <span class="p-input-icon-left">
                              <InputText v-model="filters['global'].value" placeholder="Keyword Search" />
                           </span>
                           <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined @click="clearFilter()" />
                        </div>
                     </template>
                     <template #empty> No agents yet </template>
                     <template #loading> Loading agents data. Please wait. </template>
                     <Column field="name" header="Name">
                        <template #body="{ data }">
                           {{ data.name }}
                        </template>
                     </Column>
                     <Column field="email" header="Email">
                        <template #body="{ data }">
                           {{ data.email }}
                        </template>
                     </Column>

                     <Column field="commission" header="Commission (%) (Set by agent)">
                        <template #body="{ data }">
                           {{ data.commission }}
                        </template>
                     </Column>
                     
                     <Column field="admin_commission" header="Commission (%) (Set by admin)">
                        <template #body="{ data }">
                           {{ data.admin_commission }}
                        </template>
                     </Column>

                     <Column field="loyalty.threshold" header="Loyalty Threshold">
                        <template #body="{ data }">
                           {{ data.loyalty.threshold }}
                        </template>
                     </Column>

                     <Column field="status" header="Status">
                        <template #body="{ data }">
                           {{ data.status }}
                        </template>
                     </Column>
                     
                     <Column field="credit.amount" header="Credit">
                        <template #body="{ data }">
                           {{ data.credit.amount }}
                        </template>
                     </Column>

                     <Column header="Actions" :exportable="false" class="w-[22rem]">
                        <template #body="{ data }">
                           <div class="flex items-center gap-3">
                              <button @click="handleEdit(data)" type="button" class="rounded rounded-bg bg-theme-button4-bg text-theme-button4-txt text-center px-2 py-2 uppercase text-xs">
                                 Loyalty
                              </button>
                              <button @click="handleEditAdminCommission(data)" type="button" class="whitespace-nowrap rounded rounded-bg bg-theme-button1-bg text-theme-button1-txt text-center px-2 py-2 uppercase text-xs">
                                 Commission
                              </button>
                              <button @click="toggleStatus(data)" type="button" class="whitespace-nowrap rounded rounded-bg bg-theme-button3-bg text-theme-button3-txt text-center px-2 py-2 uppercase text-xs">
                                 Status
                              </button>
                              <button @click="handleCredit(data)" type="button" class="whitespace-nowrap rounded rounded-bg bg-theme-button2-bg text-theme-button2-txt text-center px-2 py-2 uppercase text-xs">
                                 Credit
                              </button>
                           </div>
                        </template>
                     </Column>
                  </DataTable> 
               </div>
            </div>
         </div>
      </div>
   </div>
   <SlideOver :show="openDialog" @closed="openDialog = false">
      <template v-slot:content>
         <SaveAgent :agent="editAgent" action="loyalty" @success="handleSaveSuccess" @error="handleSaveError" />
      </template>
   </SlideOver>
   
   <SlideOver :show="openCommissionDialog" @closed="openCommissionDialog = false">
      <template v-slot:content>
         <SaveAgent :agent="editAgent" action="commission" @success="handleSaveSuccess" @error="handleSaveError" />
      </template>
   </SlideOver>
   
   <SlideOver :show="openToggleStatus" @closed="openToggleStatus = false">
      <template v-slot:content>
         <SaveAgent :agent="editAgent" action="status" @success="handleSaveSuccess" @error="handleSaveError" />
      </template>
   </SlideOver>
   
   <SlideOver :show="openCreditDialog" @closed="openCreditDialog = false">
      <template v-slot:content>
         <SaveAgent :agent="editAgent" action="credit" @success="handleSaveSuccess" @error="handleSaveError" />
      </template>
   </SlideOver>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { NotificationType } from '@/types/index'
import { Agent } from '@/types/admin'
import Admin from '@/composables/admin/index'
import SlideOver from '@/widgets/SlideOver.vue'
import Notification from '@/widgets/Notification.vue'
import { FilterMatchMode, FilterOperator } from 'primevue/api'
import SaveAgent from '@/components/admin/SaveAgent.vue'
import { MenuItems } from '@headlessui/vue'

const { allAgents } = Admin()
const agents = ref<Agent[] | null>(null)
const isLoading = ref<boolean>(false)
const openDialog = ref<boolean>(false)
const openCommissionDialog = ref<boolean>(false)
const openToggleStatus = ref<boolean>(false)
const openCreditDialog = ref<boolean>(false)
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})
const filters = ref()
const loading = ref<boolean>(false)
const dt = ref()
const editAgent = ref<Agent | null>(null)

onMounted(async () => {

   await fetchAgents()
})

const fetchAgents = async () => {

   const response = await allAgents()
   if (response.status == 200) {

      let items : Agent[] = []
      response.data.forEach((item: any) => {
         items.push({
            id: item.id,
            name: item.name,
            email: item.email,
            commission: item.commission,
            admin_commission: item.admin_commission,
            phone: item.phone,
            address: item.address,
            status: item.status,
            isParent: item.is_parent,
            credit: item.credit,
            loyalty: {
               redeemed: item.loyalty.redeemed,
               threshold: item.loyalty?.threshold,
            }
         })
      })

      agents.value = items
   }
}

const handleEdit = (data: Agent) => {

   editAgent.value = data
   openDialog.value = true
}

const handleEditAdminCommission = (data: Agent) => {

   editAgent.value = data
   openCommissionDialog.value = true
}

const toggleStatus = (data: Agent) => {

   editAgent.value = data
   openToggleStatus.value = true
}

const handleCredit = (data: Agent) => {

   editAgent.value = data
   openCreditDialog.value = true
}

const handleSaveSuccess = async () => {
   openDialog.value = false
   openCommissionDialog.value = false
   openToggleStatus.value = false
   openCreditDialog.value = false
   notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Agent has been saved successfully'}
   setTimeout(() => { notifClosed() }, 4000)
   
   await fetchAgents()
}

const handleSaveError = (error: string) => {
   notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
   setTimeout(() => { notifClosed() }, 4000)
}

const notifClosed = () : void => {
   notif.value.show = false
}

const initFilters = () => {
   filters.value = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS }
   }
}

initFilters()

const clearFilter = () => {
   initFilters()
}

const handleConfirmClose = () => openDialog.value = false

</script>